const HISTORY_KEY = 'loginCredentialHistory';
const MAX_HISTORY_ITEMS = 10;
const EMPTY_LIST = Object.freeze([]);

function normalizeTenantKey(tenantKey) {
	if (typeof tenantKey !== 'string') {
		return '';
	}
	return tenantKey.trim().toLowerCase();
}

function normalizeUserName(userName) {
	if (typeof userName !== 'string') {
		return '';
	}
	return userName.trim();
}

function buildCredentialPayload(raw = {}) {
	const tenantKey = normalizeTenantKey(raw.tenantKey);
	const userName = normalizeUserName(raw.userName);
	if (!tenantKey || !userName) {
		return null;
	}
	return {
		tenantKey,
		userName,
		password: typeof raw.password === 'string' ? raw.password : '',
		updatedAt: Number(raw.updatedAt) || Date.now(),
		source: typeof raw.source === 'string' ? raw.source : 'browser'
	};
}

function cloneArray(list = []) {
	return Array.isArray(list) ? list.map((item) => ({ ...item })) : [];
}

function ensureMap(raw) {
	if (!raw || typeof raw !== 'object') {
		return {};
	}
	return raw;
}

function mergeEntries(existing = [], entry) {
	const normalized = normalizeUserName(entry.userName);
	const filtered = existing.filter((item) => normalizeUserName(item.userName) !== normalized);
	filtered.unshift(entry);
	return filtered.slice(0, MAX_HISTORY_ITEMS);
}

function sortByUpdatedAt(list = []) {
	return cloneArray(list).sort((a, b) => Number(b.updatedAt) - Number(a.updatedAt));
}

const browserDriver = {
	async readTenant(tenantKey) {
		const key = normalizeTenantKey(tenantKey);
		if (!key) {
			return [];
		}
		if (typeof window === 'undefined' || !window.localStorage) {
			return [];
		}
		try {
			const cached = window.localStorage.getItem(HISTORY_KEY);
			const parsed = ensureMap(JSON.parse(cached || '{}'));
			return Array.isArray(parsed[key]) ? parsed[key] : [];
		} catch (error) {
			return [];
		}
	},
	async writeTenant(tenantKey, entries) {
		const key = normalizeTenantKey(tenantKey);
		if (!key || typeof window === 'undefined' || !window.localStorage) {
			return;
		}
		try {
			const cached = window.localStorage.getItem(HISTORY_KEY);
			const parsed = ensureMap(JSON.parse(cached || '{}'));
			parsed[key] = entries;
			window.localStorage.setItem(HISTORY_KEY, JSON.stringify(parsed));
		} catch (error) {
			// ignore storage errors
		}
	},
	async removeTenant(tenantKey) {
		const key = normalizeTenantKey(tenantKey);
		if (!key || typeof window === 'undefined' || !window.localStorage) {
			return;
		}
		try {
			const cached = window.localStorage.getItem(HISTORY_KEY);
			const parsed = ensureMap(JSON.parse(cached || '{}'));
			if (Object.prototype.hasOwnProperty.call(parsed, key)) {
				delete parsed[key];
				window.localStorage.setItem(HISTORY_KEY, JSON.stringify(parsed));
			}
		} catch (error) {
			// ignore
		}
	}
};

let storageDriver = browserDriver;

export function setStorageDriver(driver = {}) {
	storageDriver = {
		...browserDriver,
		...driver
	};
}

export async function listByTenant(payload = {}) {
	const tenantKey = normalizeTenantKey(payload.tenantKey);
	if (!tenantKey) {
		return EMPTY_LIST;
	}
	const limit = Number(payload.limit) || MAX_HISTORY_ITEMS;
	const entries = await storageDriver.readTenant(tenantKey);
	return sortByUpdatedAt(entries).slice(0, Math.max(1, limit));
}

export async function restore(payload = {}) {
	const tenantKey = normalizeTenantKey(payload.tenantKey);
	const userName = normalizeUserName(payload.userName);
	if (!tenantKey || !userName) {
		return null;
	}
	const entries = await storageDriver.readTenant(tenantKey);
	const match = entries.find((item) => normalizeUserName(item.userName) === userName);
	return match ? { ...match } : null;
}

export async function save(raw = {}) {
	const payload = buildCredentialPayload(raw);
	if (!payload) {
		return null;
	}
	if (typeof storageDriver.saveEntry === 'function') {
		await storageDriver.saveEntry(payload);
		return { ...payload };
	}
	const existing = await storageDriver.readTenant(payload.tenantKey);
	const updated = mergeEntries(existing, payload);
	await storageDriver.writeTenant(payload.tenantKey, updated);
	return { ...payload };
}

export async function remove(payload = {}) {
	const tenantKey = normalizeTenantKey(payload.tenantKey);
	const userName = normalizeUserName(payload.userName);
	if (!tenantKey || !userName) {
		return false;
	}
	if (typeof storageDriver.removeEntry === 'function') {
		return storageDriver.removeEntry({
			tenantKey,
			userName
		});
	}
	const existing = await storageDriver.readTenant(tenantKey);
	const filtered = existing.filter((item) => normalizeUserName(item.userName) !== userName);
	if (filtered.length === existing.length) {
		return false;
	}
	await storageDriver.writeTenant(tenantKey, filtered);
	return true;
}

export async function clearTenant(payload = {}) {
	const tenantKey = normalizeTenantKey(payload.tenantKey);
	if (!tenantKey) {
		return 0;
	}
	const existing = await storageDriver.readTenant(tenantKey);
	await storageDriver.removeTenant(tenantKey);
	return existing.length;
}

const loginCredentialStore = {
	listByTenant,
	restore,
	save,
	remove,
	clearTenant,
	setStorageDriver
};

export default loginCredentialStore;
